import { BriefcaseBusiness, Check, Circle, FileBadge, GraduationCap, Lightbulb } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/components/ui/accordion";
import { cn } from "@/app/handlers/cn.utils";

import { AssessedCategory, AssessedItem, FitAssessment } from "../config/api";

interface FitCoverProps {
	assessment: FitAssessment;
}

const CATEGORIES: { key: keyof FitAssessment; label: string; icon: typeof BriefcaseBusiness }[] = [
	{ key: "experience", label: "Work experience", icon: BriefcaseBusiness },
	{ key: "skills", label: "Skills", icon: Lightbulb },
	{ key: "education", label: "Education", icon: GraduationCap },
	{ key: "certifications", label: "Certifications", icon: FileBadge },
];

function statsFor(items: AssessedItem[]) {
	const required = items.filter((i) => i.necessity === "required");
	const preferred = items.filter((i) => i.necessity === "preferred");
	return {
		required: required.length,
		requiredMet: required.filter((i) => i.status === "Fit").length,
		preferred: preferred.length,
		preferredMet: preferred.filter((i) => i.status === "Fit").length,
	};
}

function RequirementRow({ item }: { item: AssessedItem }) {
	const fit = item.status === "Fit";
	const requiredMiss = !fit && item.necessity === "required";

	return (
		<li className="flex items-start gap-2 leading-snug">
			{fit ? (
				<Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
			) : (
				<Circle className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", requiredMiss ? "text-amber-500" : "text-gray-400")} />
			)}
			<span className={cn("text-xs", requiredMiss ? "text-gray-900" : fit ? "text-gray-600" : "text-gray-500")}>
				{item.item}
				{item.necessity === "preferred" && <span className="ml-1.5 text-[10px] uppercase text-gray-400">pref</span>}
			</span>
		</li>
	);
}

function CategoryRow({
	label,
	icon: Icon,
	category,
}: {
	label: string;
	icon: typeof BriefcaseBusiness;
	category: AssessedCategory;
}) {
	const s = statsFor(category.items);
	const hasRequired = s.required > 0;
	const numerator = hasRequired ? s.requiredMet : s.preferredMet;
	const denominator = hasRequired ? s.required : s.preferred;
	const pct = denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;

	const fits = category.items.filter((i) => i.status === "Fit");
	const misses = category.items.filter((i) => i.status === "Miss");

	return (
		<AccordionItem value={label} className="rounded-lg border border-gray-200 bg-white px-4">
			<AccordionTrigger className="py-3 hover:no-underline">
				<div className="flex-1 pr-3 text-left">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							<Icon className="h-4 w-4 text-gray-500" />
							<span className="text-sm font-semibold text-gray-900">{label}</span>
						</div>
						<span className="text-sm font-semibold tabular-nums text-gray-900">
							{numerator}
							<span className="text-gray-400">/{denominator}</span>
						</span>
					</div>
					<div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-100">
						<div
							className="h-full rounded-full bg-primary-700 transition-[width] duration-500"
							style={{ width: `${pct}%` }}
						/>
					</div>
					<p className="mt-2 text-xs text-gray-600">{category.summary}</p>
				</div>
			</AccordionTrigger>
			<AccordionContent>
				<div className="space-y-2.5">
					{fits.length > 0 && (
						<ul className="space-y-1.5">
							{fits.map((item, i) => (
								<RequirementRow key={`fit-${i}`} item={item} />
							))}
						</ul>
					)}
					{fits.length > 0 && misses.length > 0 && <div className="border-t border-gray-100" />}
					{misses.length > 0 && (
						<ul className="space-y-1.5">
							{misses.map((item, i) => (
								<RequirementRow key={`miss-${i}`} item={item} />
							))}
						</ul>
					)}
				</div>
				{hasRequired && s.preferred > 0 && (
					<p className="mt-2.5 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
						{s.preferredMet}/{s.preferred} preferred also met
					</p>
				)}
			</AccordionContent>
		</AccordionItem>
	);
}

function FitCover({ assessment }: FitCoverProps) {
	const totals = CATEGORIES.reduce(
		(acc, c) => {
			const s = statsFor(assessment[c.key].items);
			acc.required += s.required;
			acc.requiredMet += s.requiredMet;
			return acc;
		},
		{ required: 0, requiredMet: 0 },
	);

	return (
		<div className="space-y-5">
			<div>
				<p className="text-xs font-medium uppercase tracking-wide text-gray-500">How this employer reads your CV</p>
				<p className="mt-2 text-sm text-gray-700">
					A screener checks each requirement and finds it or does not. You show{" "}
					<span className="font-semibold text-gray-900">
						{totals.requiredMet} of {totals.required}
					</span>{" "}
					must-haves for this role. Open a category to see the gaps.
				</p>
			</div>

			<Accordion type="multiple" className="space-y-3">
				{CATEGORIES.filter(({ key }) => assessment[key].items.length > 0).map(({ key, label, icon }) => (
					<CategoryRow key={key} label={label} icon={icon} category={assessment[key]} />
				))}
			</Accordion>
		</div>
	);
}

export default FitCover;
